'use client';

import Link from '@eliadmin/core/src/components/link';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import { RiMenu5Fill } from 'react-icons/ri';
import { FaRegCircleUser } from 'react-icons/fa6';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { LanguageSelect } from '@eliadmin/core/src/components/header/language-select';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const { t } = useTranslations();
  const translatedMenuLabel = t('text3241')?.trim();
  const menuLabel = translatedMenuLabel && translatedMenuLabel !== 'text3241' ? translatedMenuLabel : 'Menu';

  const me = useCustomerStore(s => s.me);

  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label={menuLabel}
          className={cn(
            'flex h-12 w-12 items-center justify-center rounded-full bg-primary/5',
            'ea-mobile-sidebar__trigger',
            props.className?.trigger,
          )}
        >
          <RiMenu5Fill size={20} className="text-black" />
        </SheetTrigger>
        <SheetContent
          side={'left'}
          className={cn(
            'flex h-fit w-full max-w-[800px] flex-col items-center justify-center gap-10 px-5 pb-10 pt-[88px] sm:max-w-[800px]',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
          closeIcon={{
            wrapper:
              'left-5 top-2.5 w-12 h-12 rounded-full bg-primary/5 flex items-center justify-center',
            icon: 'text-primary h-6 w-6 font-bold',
          }}
        >
          <SheetClose asChild className="w-full">
            <Link prefetchOnHover
              prefetch={false}
              href={ROUTES.MEMBER_ZONE()}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-primary/5 text-sm font-semibold text-primary"
            >
              <FaRegCircleUser size={20} /> {t('text2398')}
            </Link>
          </SheetClose>
          <div className="flex w-full flex-col items-center justify-center gap-5">
            {props.menu?.map((link, index) => (
              <SheetClose
                asChild
                className="flex w-full items-center justify-center"
                key={link.name}
              >
                <Link prefetchOnHover
                  prefetch={false}
                  href={getSafeHref(link?.path ?? '')}
                  className="text-base font-semibold text-primary"
                >
                  {link.name}{' '}
                </Link>
              </SheetClose>
            ))}
          </div>
          <div className="flex w-full flex-col items-center justify-center gap-6">
            <SheetClose asChild>
              <Link prefetchOnHover prefetch={false} href={'/'} className="text-base font-semibold text-secondary">
                {t('text2396')}
              </Link>
            </SheetClose>
            <div className="flex w-full items-center justify-end">
              <LanguageSelect />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
